<template>
    <div class="patient-profile">
        <!-- Botones para agregar campos -->
        <div v-if="inactiveFields.length > 0" class="add-fields-section mb-4">
            <button
v-for="field in inactiveFields" :key="field.name" class="add-field-btn"
                :title="'Agregar ' + field.label" @click="addField(field.name)">
                <i class="fas fa-plus"></i>
                <span>{{ field.label }}</span>
            </button>
        </div>

        <!-- Campos activos -->
        <div v-if="activeFields.length > 0" class="fields-grid-compact">
            <div v-for="field in activeFields" :key="field.name" class="field-group-compact mb-3">
                <div class="field-header">
                    <label class="form-label-modern">
                        <span class="label-content-modern">
                            <i :class="field.icon"></i>
                            <span>{{ field.label }}</span>
                        </span>
                    </label>
                    <button class="close-field-btn" title="Cerrar campo" @click="removeField(field.name)">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- Input simple para campos de texto -->
                <input
v-if="field.type === 'text'" v-model="formData[field.name]" type="text" class="form-control"
                    :placeholder="field.placeholder || ''" />

                <!-- Editor Quill para campos de descripción -->
                <div v-else-if="field.type === 'editor'" class="editor-wrapper">
                    <quill-editor v-model:value="formData[field.name]" :options="$editorOptions" class="editor" />
                    <div class="editor-footer">
                        <div class="editor-stats">
                            <span class="stat-item">
                                <i class="fas fa-font"></i>
                                {{ getTextLength(formData[field.name]) }} caracteres
                            </span>
                            <span class="stat-item">
                                <i class="fas fa-align-left"></i>
                                {{ getWordCount(formData[field.name]) }} palabras
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { quillEditor } from 'vue3-quill';
import api from '@/services/api';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    }
});

// Funciones para contar caracteres y palabras
const getTextLength = (html) => {
    if (!html) return 0;
    const text = html.replace(/<[^>]*>/g, '');
    return text.length;
};

const getWordCount = (html) => {
    if (!html) return 0;
    const text = html.replace(/<[^>]*>/g, '').trim();
    if (!text) return 0;
    return text.split(/\s+/).length;
};

// Definir todos los campos disponibles
const fields = ref([
    { name: 'producto_gesta_descripcion', label: 'Producto de la gesta', type: 'editor', icon: 'fas fa-baby', isActive: false },
    { name: 'llanto_espontaneo_descripcion', label: 'Llanto espontáneo al nacer', type: 'editor', icon: 'fas fa-volume-up', isActive: false },
    { name: 'nacimiento_descripcion', label: 'Nacimiento', type: 'editor', icon: 'fas fa-heartbeat', isActive: false },
    { name: 'control_embrazo_descripcion', label: 'Control del embarazo', type: 'editor', icon: 'fas fa-hospital', isActive: false },
    { name: 'duracion_embrazo_descripcion', label: 'Duración del embarazo', type: 'editor', icon: 'fas fa-calendar-alt', isActive: false },
    { name: 'peso', label: 'Peso al nacer', type: 'text', icon: 'fas fa-weight', placeholder: 'Ejemplo: 3.5 kg', isActive: false },
    { name: 'talla_nacer', label: 'Talla al nacer', type: 'text', icon: 'fas fa-ruler-vertical', placeholder: 'Ejemplo: 50 cm', isActive: false },
    { name: 'incubadora_descripcion', label: 'Incubadora', type: 'editor', icon: 'fas fa-thermometer-half', isActive: false },
    { name: 'complicaciones_descripcion', label: 'Complicaciones', type: 'editor', icon: 'fas fa-exclamation-triangle', isActive: false },
    { name: 'inmunizaciones_descripcion', label: 'Inmunizaciones', type: 'editor', icon: 'fas fa-syringe', isActive: false },
    { name: 'quirurgicos_descripcion', label: 'Quirúrgicos', type: 'editor', icon: 'fas fa-scissors', isActive: false },
    { name: 'transfusionales_descripcion', label: 'Transfusionales', type: 'editor', icon: 'fas fa-tint', isActive: false },
    { name: 'traumaticos_descripcion', label: 'Traumáticos', type: 'editor', icon: 'fas fa-band-aid', isActive: false },
    { name: 'lactancia_descripcion', label: 'Lactancia', type: 'editor', icon: 'fas fa-baby-carriage', isActive: false },
    { name: 'alimentacion_actual_descripcion', label: 'Alimentación actual', type: 'editor', icon: 'fas fa-utensils', isActive: false },
    { name: 'inicio_aprobacion_descripcion', label: 'Inicio de ablactación', type: 'editor', icon: 'fas fa-apple-alt', isActive: false },
    { name: 'hospitalizaciones_descripcion', label: 'Hospitalizaciones', type: 'editor', icon: 'fas fa-hospital-user', isActive: false },
    { name: 'exantematicas_descripcion', label: 'Enfermedades exantemáticas', type: 'editor', icon: 'fas fa-virus', isActive: false },
    { name: 'sostiene_cabeza_descripcion', label: 'Sostiene cabeza', type: 'editor', icon: 'fas fa-head-side', isActive: false },
    { name: 'se_sienta_solo_descripcion', label: 'Se sienta solo', type: 'editor', icon: 'fas fa-chair', isActive: false },
    { name: 'gatea_descripcion', label: 'Gatea', type: 'editor', icon: 'fas fa-child', isActive: false },
    { name: 'camina_solo_descripcion', label: 'Camina solo', type: 'editor', icon: 'fas fa-walking', isActive: false },
    { name: 'corre_descripcion', label: 'Corre', type: 'editor', icon: 'fas fa-running', isActive: false },
    { name: 'se_para_con_apoyo_descripcion', label: 'Se para con apoyo', type: 'editor', icon: 'fas fa-user-shield', isActive: false },
    { name: 'balbucea_descripcion', label: 'Balbucea', type: 'editor', icon: 'fas fa-comment-dots', isActive: false },
    { name: 'primeras_palabras_descripcion', label: 'Primeras palabras', type: 'editor', icon: 'fas fa-comments', isActive: false },
    { name: 'control_esfinter_descripcion', label: 'Control de esfínter', type: 'editor', icon: 'fas fa-toilet', isActive: false },
    { name: 'piel_descripcion', label: 'Piel', type: 'editor', icon: 'fas fa-allergies', isActive: false },
    { name: 'digestivo_descripcion', label: 'Digestivo', type: 'editor', icon: 'fas fa-stomach', isActive: false },
    { name: 'cardiovascular_descripcion', label: 'Cardiovascular', type: 'editor', icon: 'fas fa-heart', isActive: false },
    { name: 'genito_urinario_descripcion', label: 'Genito urinario', type: 'editor', icon: 'fas fa-kidneys', isActive: false },
    { name: 'musculo_esqueletico_descripcion', label: 'Músculo esquelético', type: 'editor', icon: 'fas fa-bone', isActive: false },
    { name: 'neuropsiquiatrico_descripcion', label: 'Neuropsiquiátrico', type: 'editor', icon: 'fas fa-brain', isActive: false },
    { name: 'linfohematico_descripcion', label: 'Linfohemático', type: 'editor', icon: 'fas fa-heartbeat', isActive: false },
    { name: 'nervioso_descripcion', label: 'Nervioso', type: 'editor', icon: 'fas fa-network-wired', isActive: false },
    { name: 'habitos_higienicos_descripcion', label: 'Hábitos higiénicos', type: 'editor', icon: 'fas fa-hands-wash', isActive: false },
    { name: 'hospitalizaciones_extra_descripcion', label: 'Hospitalizaciones adicionales', type: 'editor', icon: 'fas fa-procedures', isActive: false },
    { name: 'alergias_intolerancias_descripcion', label: 'Alergias e intolerancias', type: 'editor', icon: 'fas fa-allergies', isActive: false },
    { name: 'antecedentes_transfuncionales_descripcion', label: 'Antecedentes transfuncionales', type: 'editor', icon: 'fas fa-file-medical', isActive: false },
    { name: 'fracturas_descripcion', label: 'Fracturas', type: 'editor', icon: 'fas fa-x-ray', isActive: false },
    { name: 'antecedentes_quirurgicos_descripcion', label: 'Antecedentes quirúrgicos', type: 'editor', icon: 'fas fa-user-md', isActive: false },
    { name: 'exantematicas_extra_descripcion', label: 'Exantemáticas adicionales', type: 'editor', icon: 'fas fa-disease', isActive: false },
    { name: 'observaciones_descripcion', label: 'Observaciones', type: 'editor', icon: 'fas fa-notes-medical', isActive: false }
]);

// Computed para campos activos e inactivos
const activeFields = computed(() => fields.value.filter(f => f.isActive));
const inactiveFields = computed(() => fields.value.filter(f => !f.isActive));

// Funciones para agregar/remover campos
const addField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) field.isActive = true;
};

const removeField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) field.isActive = false;
};

// FormData reactivo
const formData = ref({});

const loadData = async () => {
    try {
        const response = await api.get(`/pacientes/${props.patientRecord.id}`);
        const data = response.data.data.pediatric_history || {};

        // Asignar datos y activar campos que tienen valor
        Object.assign(formData.value, data);

        fields.value.forEach(field => {
            if (formData.value[field.name]) {
                field.isActive = true;
            }
        });
    } catch (error) {
        console.error('Error al cargar historia pediátrica:', error);
    }
};

const saveData = async () => {
    try {
        await api.put(`/pacientes/${props.patientRecord.id}/pediatric-history`, {
            pediatric_history: formData.value
        });
    } catch (error) {
        console.error('Error al guardar los datos del paciente:', error);
    }
};

watch(formData, saveData, { deep: true });

// Watch para detectar cambios de paciente
watch(() => props.patientRecord.id, (newId, oldId) => {
    if (newId !== oldId) {
        console.log('🔄 Cambio de paciente detectado en PatientPediatria:', oldId, '->', newId);
        // Resetear formData antes de cargar nuevos datos
        formData.value = {};
        // Resetear campos activos
        fields.value.forEach(field => {
            field.isActive = false;
        });
        loadData();
    }
});

onMounted(() => {
    loadData();
});
</script>

<style scoped>
.patient-profile {
    background-color: #fff;
    border-radius: 8px;
    padding: 20px;
}

/* Botones para agregar campos */
.add-fields-section {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
}

.add-field-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-field-btn:hover {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.add-field-btn i {
    font-size: 11px;
}

/* Campos activos */
.fields-grid-compact {
    display: grid;
    gap: 15px;
}

.field-group-compact {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 15px;
    transition: all 0.2s ease;
}

.field-group-compact:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.field-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.form-label-modern {
    margin: 0;
    font-weight: 500;
    color: #1e293b;
}

.label-content-modern {
    display: flex;
    align-items: center;
    gap: 8px;
}

.label-content-modern i {
    color: #3b82f6;
    width: 16px;
    text-align: center;
}

.close-field-btn {
    background: #fee2e2;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.2s ease;
}

.close-field-btn:hover {
    background: #dc2626;
    color: white;
}

.close-field-btn i {
    font-size: 12px;
}

/* Editor styles */
.editor-wrapper {
    position: relative;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    transition: all 0.3s ease;
}

.editor-wrapper:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.editor-wrapper:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.editor-wrapper :deep(.ql-container) {
    border: none;
    font-size: 14px;
    min-height: 120px;
}

.editor-wrapper :deep(.ql-editor) {
    min-height: 120px;
    padding: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.6;
    color: #1e293b;
}

.editor-wrapper :deep(.ql-editor.ql-blank::before) {
    color: #94a3b8;
    font-style: normal;
    left: 16px;
}

.editor-wrapper :deep(.ql-toolbar) {
    border: none;
    border-bottom: 1px solid #e0e0e0;
    background: #f8fafc;
    padding: 10px;
}

.editor-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    background: #f8fafc;
    border-top: 1px solid #e0e0e0;
}

.editor-stats {
    display: flex;
    gap: 20px;
    font-size: 12px;
    color: #64748b;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.stat-item i {
    font-size: 11px;
    color: #94a3b8;
}

/* Form controls */
.form-control {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Indicador de auto-guardado */
.autosave-indicator {
    min-width: 200px;
}

.autosave-indicator .badge {
    font-size: 12px;
    padding: 6px 10px;
    font-weight: 500;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.btn-primary {
    background-color: #007bff;
    border-color: #007bff;
    padding: 8px 16px;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.3s ease;
}

.btn-primary:hover {
    background-color: #0056b3;
    border-color: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-primary:active {
    transform: translateY(0);
}
</style>
